/**
 * Operator page: push the tenant's pool images to Google Ads as reusable
 * ImageAssets for PMax (#11). Browse the client_image_assets pool, search,
 * filter, multi-select (with shift-click range select), and push.
 *
 * Recycles the webapp's list patterns: the debounced search input style from
 * AssetFilterRail, the `useIssueSelection` hook (toggle/toggleAll/selectRange),
 * and the fixed bottom bulk-action bar (QABulkActionBar). HEIC/HEIF images are
 * converted to JPEG (POST /client-image-assets/:id/convert) to become
 * previewable AND pushable.
 */
import { fuzzyFilter } from '@/lib/fuzzyMatch';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ImageIcon,
  Loader2,
  UploadCloud,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Search,
  Maximize2,
  X,
  Link2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useIssueSelection } from '@/hooks/useIssueSelection';
import { useDebounce } from '@/hooks/use-debounce';
import {
  clientImageAssetsService,
  type ClientImageAssetListItem,
} from '@/services/clientImageAssetsService';
import {
  googleAdsImageAssetsService,
  type ImageAssetSyncResponse,
} from '@/services/googleAdsImageAssetsService';

const OK_STATUSES = new Set(['success', 'reused', 'dry_run']);
const PUSHABLE_EXT = new Set(['jpg', 'jpeg', 'png']);
const HEIC_EXT = new Set(['heic', 'heif']);

type AssetKind = 'pushable' | 'heic' | 'other';
type PoolFilter = 'all' | 'ready' | 'heic';
type SortBy = 'newest' | 'oldest' | 'name-asc' | 'name-desc';

function classify(it: ClientImageAssetListItem): AssetKind {
  const ext = (it.extension || '').toLowerCase();
  if (PUSHABLE_EXT.has(ext)) return 'pushable';
  if (HEIC_EXT.has(ext)) return 'heic';
  return 'other';
}

function extractDetail(err: unknown): {
  flag?: string;
  code?: string;
  message?: string;
  text?: string;
} {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (detail && typeof detail === 'object') return detail as Record<string, string>;
  if (typeof detail === 'string') return { text: detail };
  return {};
}

/** One image tile. Pushable tiles are selectable (checkbox + shift-click);
 * HEIC tiles offer Convert (or show a "converted" badge). Manages its own <img>
 * load-error state so a broken signed URL degrades to an explanatory placeholder. */
function AssetTile({
  item,
  kind,
  isSelected,
  resultStatus,
  convertPending,
  isConverted,
  onClick,
  onConvert,
  onExpand,
}: {
  item: ClientImageAssetListItem;
  kind: AssetKind;
  isSelected: boolean;
  resultStatus?: string;
  convertPending: boolean;
  isConverted: boolean;
  onClick: (e: React.MouseEvent) => void;
  onConvert: () => void;
  onExpand: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const isHeic = kind === 'heic';
  const showImage = !isHeic && !!item.url && !imgError;

  const reason = isHeic
    ? isConverted
      ? 'HEIC — converted to JPEG ✓'
      : 'HEIC — no inline preview'
    : !item.url
      ? 'No preview — storage unavailable'
      : imgError
        ? 'Preview unavailable — storage error'
        : '';

  return (
    <div
      className={`group relative select-none overflow-hidden rounded-lg border text-left transition-colors ${
        isSelected ? 'border-coral ring-2 ring-ring' : 'border-border'
      } ${isHeic ? '' : 'cursor-pointer hover:border-coral'}`}
      onClick={isHeic ? undefined : onClick}
    >
      <div className="relative aspect-square w-full bg-muted">
        {showImage ? (
          <img
            src={item.url as string}
            alt={item.alt_text || item.filename}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
            draggable={false}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 p-2 text-center text-muted-foreground">
            <ImageIcon className="h-6 w-6 opacity-70" />
            <span className="text-[10px] leading-tight">{reason}</span>
            {isHeic &&
              (isConverted ? (
                <span className="mt-1 flex items-center gap-1 text-[10px] font-medium text-coral">
                  <CheckCircle2 className="h-3.5 w-3.5" /> JPEG added to pool
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1 h-7 gap-1 text-xs"
                  disabled={convertPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    onConvert();
                  }}
                >
                  {convertPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Convert to JPEG
                </Button>
              ))}
          </div>
        )}
        {isHeic && (
          <span className="absolute right-1 top-1 rounded bg-teal px-1 text-[9px] font-medium uppercase text-white">
            HEIC
          </span>
        )}
        {showImage && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            aria-label="View full screen"
            title="View full screen"
            className="absolute right-1 top-1 hidden rounded bg-black/55 p-1 text-white transition-colors hover:bg-black/75 group-hover:block"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-1 p-2">
        <span className="truncate text-xs text-foreground" title={item.filename}>
          {item.filename}
        </span>
        {resultStatus &&
          (OK_STATUSES.has(resultStatus) ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-coral" aria-label={resultStatus} />
          ) : (
            <XCircle className="h-4 w-4 shrink-0 text-destructive" aria-label={resultStatus} />
          ))}
      </div>

      {!isHeic && (
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          tabIndex={-1}
          className="absolute left-2 top-2 h-4 w-4 accent-coral"
        />
      )}
    </div>
  );
}

export default function GoogleAdsImageAssetsPage() {
  const currentTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = currentTenant ?? '';
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  // Only treat as "no ads account" when we actually know (workspace loaded) —
  // otherwise assume connected so we never wrongly nag/disable.
  const noAdsAccount = !!currentWorkspace && !currentWorkspace.has_google_ads;
  const [dryRun, setDryRun] = useState(true);
  const [poolFilter, setPoolFilter] = useState<PoolFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('newest');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [result, setResult] = useState<ImageAssetSyncResponse | null>(null);
  const [lightbox, setLightbox] = useState<ClientImageAssetListItem | null>(null);
  const [addUrl, setAddUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const poolQuery = useQuery({
    queryKey: ['ga-image-pool', tenantId],
    // The list endpoint caps limit at 100, so page through (up to 500) to hold
    // the whole pool client-side (needed for cross-tile shift-range selection).
    queryFn: async () => {
      const PAGE = 100;
      const MAX = 500;
      const items: ClientImageAssetListItem[] = [];
      let offset = 0;
      let total = 0;
      for (;;) {
        const page = await clientImageAssetsService.list(tenantId, {
          limit: PAGE,
          offset,
          sign: true,
          // Show images the user just uploaded/imported (they land as
          // needs_review, which the default "safe-to-use" list hides).
          include_review: true,
        });
        total = page.total;
        items.push(...page.items);
        offset += PAGE;
        if (page.items.length < PAGE || items.length >= Math.min(total, MAX)) break;
      }
      return { tenant_id: tenantId, total, limit: PAGE, offset: 0, items };
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });

  const allItems = poolQuery.data?.items ?? [];

  // Search (client-side over the loaded pool) — filename + alt text.
  // lib/fuzzyMatch, STRICT: the selection below is pushed to Google Ads, so
  // no letters-in-order or typo match may put an unrelated image in it
  // (reviewer-2 on #2996). The "Sort by" dropdown decides the order.
  const searched = useMemo(
    () =>
      fuzzyFilter(allItems, debouncedSearch, (it) => [it.filename, it.alt_text], {
        sort: false,
        strict: true,
      }),
    [allItems, debouncedSearch],
  );

  const sorted = useMemo(() => {
    const arr = [...searched];
    const name = (it: ClientImageAssetListItem) => (it.filename || '').toLowerCase();
    const created = (it: ClientImageAssetListItem) => it.created_at || '';
    switch (sortBy) {
      case 'oldest':
        return arr.sort((a, b) => created(a).localeCompare(created(b)));
      case 'name-asc':
        return arr.sort((a, b) => name(a).localeCompare(name(b)));
      case 'name-desc':
        return arr.sort((a, b) => name(b).localeCompare(name(a)));
      default: // newest
        return arr.sort((a, b) => created(b).localeCompare(created(a)));
    }
  }, [searched, sortBy]);

  const { pushable, heic, otherCount } = useMemo(() => {
    const p: ClientImageAssetListItem[] = [];
    const h: ClientImageAssetListItem[] = [];
    let other = 0;
    for (const it of sorted) {
      const k = classify(it);
      if (k === 'pushable') p.push(it);
      else if (k === 'heic') h.push(it);
      else other += 1;
    }
    return { pushable: p, heic: h, otherCount: other };
  }, [sorted]);

  // Previewable (JPG/PNG) first; the filter narrows to one group.
  const visible = useMemo(() => {
    if (poolFilter === 'ready') return pushable;
    if (poolFilter === 'heic') return heic;
    return [...pushable, ...heic];
  }, [poolFilter, pushable, heic]);

  // A HEIC is "converted" when a JPG twin (same basename) exists — data-driven
  // so the badge survives a refresh.
  const pushableNames = useMemo(
    () => new Set(pushable.map((it) => (it.filename || '').toLowerCase())),
    [pushable],
  );
  const isHeicConverted = (it: ClientImageAssetListItem) =>
    pushableNames.has(
      (it.filename || '').replace(/\.(heic|heif)$/i, '.jpg').toLowerCase(),
    );

  // Only pushable (JPG/PNG) tiles are selectable; feed them to the selection
  // hook in display order so shift-range selection is contiguous & correct.
  const selectableIds = useMemo(
    () => visible.filter((it) => classify(it) === 'pushable').map((it) => it.id),
    [visible],
  );
  const selection = useIssueSelection(selectableIds);
  // Shown when a search change dropped a selection, until the next selection.
  const [selectionCleared, setSelectionCleared] = useState(false);
  // Adjusted during render (not in an effect): a new selection hides the note.
  if (selectionCleared && selection.selectedCount > 0) setSelectionCleared(false);

  const resultById = useMemo(() => {
    const m = new Map<string, string>();
    result?.results.forEach((r) => m.set(r.asset_id, r.status));
    return m;
  }, [result]);

  const handleTileClick = (id: string, e: React.MouseEvent) => {
    if (e.shiftKey && anchorId && anchorId !== id) {
      selection.selectRange(anchorId, id);
    } else {
      selection.toggle(id);
    }
    setAnchorId(id);
  };

  const syncMutation = useMutation({
    mutationFn: () =>
      googleAdsImageAssetsService.sync(tenantId, selection.selectedIds, dryRun),
    onSuccess: (res) => {
      setResult(res);
      const { uploaded, dry_run, failed } = res.summary;
      toast.success(`${uploaded} uploaded · ${dry_run} dry-run · ${failed} failed`);
      selection.clear();
    },
    onError: (err: unknown) => {
      const d = extractDetail(err);
      if (d.flag) toast.error(`Feature disabled: ${d.flag}`);
      else toast.error(d.message || d.text || 'Failed to push image assets');
    },
  });

  const convertMutation = useMutation({
    mutationFn: (assetId: string) => clientImageAssetsService.convertHeic(tenantId, assetId),
    onSuccess: (res) => {
      toast.success(
        res.reused
          ? `Already converted → ${res.filename}`
          : `Converted to ${res.filename} — now previewable & pushable`,
      );
      poolQuery.refetch();
    },
    onError: (err: unknown) => {
      const d = extractDetail(err);
      if (d.flag) toast.error(`HEIC conversion disabled (${d.flag})`);
      else if (d.code === 'source_missing' || d.code === 'storage_error')
        toast.error('Conversion failed — image storage is unavailable');
      else toast.error(d.message || d.text || 'Failed to convert image');
    },
  });

  const importUrlMutation = useMutation({
    mutationFn: (url: string) => clientImageAssetsService.importFromUrl(tenantId, url),
    onSuccess: (res) => {
      toast.success(`Added ${res.filename} to the pool`);
      setAddUrl('');
      poolQuery.refetch();
    },
    onError: (err: unknown) => {
      const d = extractDetail(err);
      toast.error(d.message || d.text || 'Could not add image from that link');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => clientImageAssetsService.uploadFiles(tenantId, files),
    onSuccess: (res) => {
      // Show the server's per-file rejection reason(s) — the backend returns
      // a clear reason on each rejected file (wrong extension, MIME mismatch,
      // over size cap, decompression-bomb, etc.); don't collapse to "format/size".
      const reasons = Array.from(
        new Set(
          (res.results || [])
            .filter((r) => r.status === 'rejected' && r.reason)
            .map((r) => r.reason as string),
        ),
      );
      if (res.stored > 0)
        toast.success(
          `Uploaded ${res.stored} image${res.stored === 1 ? '' : 's'}` +
            (res.rejected
              ? ` · ${res.rejected} rejected${reasons.length ? ` (${reasons.join('; ')})` : ''}`
              : ''),
        );
      else
        toast.error(
          reasons.length
            ? `All ${res.rejected} file(s) rejected — ${reasons.join('; ')}`
            : `All ${res.rejected} file(s) rejected — check format/size`,
        );
      poolQuery.refetch();
    },
    onError: (err: unknown) => {
      const d = extractDetail(err);
      toast.error(d.message || d.text || 'Upload failed');
    },
  });

  if (!tenantId) {
    return (
      <div className="w-full text-muted-foreground">
        Select a workspace to manage its Google Ads image assets.
      </div>
    );
  }

  const filterChips: [PoolFilter, string][] = [
    ['all', `All (${pushable.length + heic.length})`],
    ['ready', `With preview · JPG/PNG (${pushable.length})`],
    ['heic', `HEIC — needs convert (${heic.length})`],
  ];

  return (
    <div className="w-full space-y-5 pb-24">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold text-foreground">Google Ads Image Assets</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Push this client's images to their Google Ads account as reusable assets for
          Performance Max. Select multiple (Shift-click for a range), then push. HEIC
          images can be converted to JPEG to make them previewable &amp; pushable.
        </p>
      </header>

      {noAdsAccount && (
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          This client has no connected Google Ads account yet. You can browse, add,
          and convert images here — connect a Google Ads account to push them.
        </div>
      )}

      {/* Add images: URL / Google Drive link / upload */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <div className="relative min-w-[240px] flex-1">
          <Link2 className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={addUrl}
            onChange={(e) => setAddUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && addUrl.trim() && !importUrlMutation.isPending)
                importUrlMutation.mutate(addUrl.trim());
            }}
            placeholder="Paste an image URL or Google Drive link…"
            className="pl-8"
          />
        </div>
        <Button
          variant="outline"
          className="gap-2"
          disabled={!addUrl.trim() || importUrlMutation.isPending}
          onClick={() => importUrlMutation.mutate(addUrl.trim())}
        >
          {importUrlMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}
          Add from link
        </Button>
        <span className="text-xs text-muted-foreground">or</span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) uploadMutation.mutate(files);
            e.target.value = '';
          }}
        />
        <Button
          variant="outline"
          className="gap-2"
          disabled={uploadMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Upload files
        </Button>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              // A new search must not leave images it now hides selected for
              // the push, so the selection goes, with a note.
              if (selection.selectedCount > 0) {
                selection.clear();
                setSelectionCleared(true);
              }
            }}
            placeholder="Search by filename…"
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filterChips.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setPoolFilter(key)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                poolFilter === key
                  ? 'border-coral bg-coral text-white'
                  : 'border-border text-muted-foreground hover:border-coral'
              }`}
            >
              {label}
            </button>
          ))}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            aria-label="Sort images"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
          </select>
        </div>
      </div>

      {selectionCleared && selection.selectedCount === 0 && (
        <p className="text-xs text-muted-foreground" role="status">
          Selection cleared because the search changed.
        </p>
      )}

      {/* Select-all row */}
      {selectableIds.length > 0 && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-coral"
              checked={selection.isAllSelected}
              ref={(el) => {
                if (el)
                  el.indeterminate =
                    selection.selectedCount > 0 && !selection.isAllSelected;
              }}
              onChange={() => selection.toggleAll()}
            />
            Select all with preview ({selectableIds.length})
          </label>
          {otherCount > 0 && (
            <span className="text-muted-foreground/70">
              · {otherCount} hidden (unsupported)
            </span>
          )}
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-border bg-card p-3 text-sm">
          <span className="font-medium">Result</span> · customer {result.customer_id} ·{' '}
          <span className="text-coral">{result.summary.uploaded} uploaded</span> ·{' '}
          {result.summary.dry_run} dry-run ·{' '}
          <span className="text-destructive">{result.summary.failed} failed</span>
        </div>
      )}

      {poolQuery.isLoading ? (
        <div className="flex items-center gap-2 p-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading image pool…
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          {allItems.length > 0
            ? debouncedSearch.trim()
              ? `No images match "${debouncedSearch.trim()}".`
              : `No usable images. All ${allItems.length} in this pool are unsupported formats.`
            : "No images in this client's pool yet. Populate the image library first."}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {visible.map((it) => {
            const kind = classify(it);
            return (
              <AssetTile
                key={it.id}
                item={it}
                kind={kind}
                isSelected={selection.isSelected(it.id)}
                resultStatus={resultById.get(it.id)}
                convertPending={
                  convertMutation.isPending && convertMutation.variables === it.id
                }
                isConverted={kind === 'heic' && isHeicConverted(it)}
                onClick={(e) => handleTileClick(it.id, e)}
                onConvert={() => convertMutation.mutate(it.id)}
                onExpand={() => setLightbox(it)}
              />
            );
          })}
        </div>
      )}

      {/* Bulk action bar (fixed) */}
      {selection.selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between gap-4 border-t border-border bg-background px-6 py-3 shadow-lg animate-in slide-in-from-bottom-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-foreground">
              {selection.selectedCount} selected
            </span>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 accent-coral"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry run (validate &amp; log, no live write)
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => selection.clear()}>
              Clear
            </Button>
            <Button
              className="gap-2"
              disabled={syncMutation.isPending || noAdsAccount}
              title={
                noAdsAccount
                  ? 'Connect a Google Ads account for this client to push'
                  : undefined
              }
              onClick={() => syncMutation.mutate()}
            >
              {syncMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="h-4 w-4" />
              )}
              {noAdsAccount
                ? 'No Google Ads account'
                : dryRun
                  ? 'Validate for Google Ads'
                  : 'Push to Google Ads'}
            </Button>
          </div>
        </div>
      )}

      {/* Full-screen lightbox */}
      {lightbox && lightbox.url && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close full screen"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <figure className="flex max-h-full max-w-full flex-col items-center gap-3">
            <img
              src={lightbox.url}
              alt={lightbox.alt_text || lightbox.filename}
              className="max-h-[85vh] max-w-[92vw] rounded object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <figcaption className="max-w-[92vw] truncate text-sm text-white/80">
              {lightbox.filename}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
